// Bilingual content deck for SAU landing page
// English is the primary source. Arabic copy is prepared as a structured fallback
// so that the page can switch cleanly between LTR and RTL.

export const content = {
    en: {
        meta: {
            brand: "SAU",
            brandFull: "Syrian AI University",
            dir: "ltr",
            lang: "en",
        },
        nav: {
            links: [
                { id: "about", label: "About" },
                { id: "offerings", label: "Offerings" },
                { id: "audience", label: "Audience" },
                { id: "vision", label: "Vision" },
                { id: "launch", label: "Launch" },
            ],
            cta: "Join Us",
            home: "Home",
        },
        hero: {
            badge: "Launching Soon",
            eyebrow: "A Syrian Educational Initiative",
            title: "Syrian AI University",
            titleArabic: "جامعة الذكاء الاصطناعي السورية",
            supporting:
                "A Syrian initiative to make artificial intelligence learning practical, accessible, and future-ready.",
            description:
                "Syrian AI University is an upcoming educational initiative created to help students, young people, and learners from all segments of society understand and use artificial intelligence tools in a simple and practical way — through workshops, structured educational content, and useful resources that support real application in study, work, and everyday life.",
            primaryCta: "Join Us",
            secondaryCta: "Launching Soon",
            pillars: ["Educational", "Practical", "Forward-looking"],
        },
        about: {
            eyebrow: "About",
            title: "What is Syrian AI University?",
            body:
                "Syrian AI University is a youth-driven Syrian initiative focused on spreading practical awareness and meaningful learning in artificial intelligence. It is being developed to become a gateway where learners can understand modern AI tools, discover how to use them effectively, and benefit from them in education, productivity, research, creativity, and digital work.",
            pillars: [
                { label: "Practical", detail: "Learning built for real use — not abstract theory." },
                { label: "Accessible", detail: "A clear starting point for curious minds." },
                { label: "Future-ready", detail: "A local initiative with a forward-looking purpose." },
            ],
        },
        whyMatters: {
            eyebrow: "Why It Matters",
            title: "Why does this initiative matter?",
            body:
                "Artificial intelligence is now shaping the future of education, work, research, and communication. Yet many learners still need a clear, practical, and accessible starting point that explains these tools in a simple and relevant way. Syrian AI University aims to bridge this gap by making AI knowledge closer to people and easier to apply.",
            pointers: [
                { k: "01", t: "Closer to people", d: "AI knowledge framed around real needs, not jargon." },
                { k: "02", t: "Practical by design", d: "Learning that moves from curiosity to real application." },
                { k: "03", t: "Rooted locally", d: "A Syrian gateway into the global AI conversation." },
            ],
        },
        offerings: {
            eyebrow: "The Platform",
            title: "What will the platform offer?",
            intro:
                "The platform is being prepared to provide a practical learning experience that helps users move from curiosity to real understanding and useful application.",
            items: [
                {
                    icon: "workshop",
                    title: "Practical Workshops",
                    body:
                        "Live or structured workshops that simplify AI tools and show how they can be used in real situations.",
                },
                {
                    icon: "resource",
                    title: "Learning Resources",
                    body:
                        "Curated materials and files that support self-learning and make the learning journey clearer and easier.",
                },
                {
                    icon: "apply",
                    title: "Real Applications",
                    body:
                        "Examples and practical use cases showing how AI can support study, work, content creation, research, and productivity.",
                },
                {
                    icon: "guidance",
                    title: "Accessible Guidance",
                    body:
                        "A learning direction designed for beginners and curious learners who want a practical entry point into AI.",
                },
            ],
        },
        audience: {
            eyebrow: "Who It’s For",
            title: "Who is this initiative for?",
            body:
                "Syrian AI University is designed for everyone who wants to understand and use artificial intelligence in a meaningful way. It is not limited to university students, but open to all learners, youth, researchers, creators, and ambitious individuals who want to keep pace with the future.",
            items: [
                { title: "University Students", body: "Broaden your academic edge with AI fluency." },
                { title: "Researchers", body: "Integrate AI into inquiry, analysis, and discovery." },
                { title: "Young Professionals", body: "Move faster at work with practical AI skills." },
                { title: "Creators & Innovators", body: "Use AI to build, design, and tell stories." },
                { title: "Anyone Interested in AI", body: "Curiosity is the only prerequisite." },
            ],
        },
        vision: {
            eyebrow: "Vision",
            title: "Our Vision",
            body:
                "To build a Syrian educational gateway that makes artificial intelligence learning accessible, practical, and inspiring for the next generation.",
        },
        mission: {
            eyebrow: "Mission",
            title: "Our Mission",
            body:
                "To simplify AI concepts, introduce useful tools, and create an educational environment that empowers learners to benefit from modern technology with confidence and clarity.",
        },
        identity: {
            eyebrow: "Our Purpose",
            title: "Why Syrian AI University?",
            body:
                "Because the future should be understood, not feared. Because access to AI knowledge should be clearer, fairer, and closer to the real needs of society. Syrian AI University is being shaped to become a meaningful local initiative with a forward-looking educational purpose.",
            quote: "The future should be understood — not feared.",
        },
        collaboration: {
            eyebrow: "Collaboration",
            title: "Developed and supported through collaboration",
            intro:
                "This initiative is being developed through collaboration with academic and community entities that contribute to enabling its presence and growth.",
            leadLabel: "Developed by",
            lead: "Syrian AI Society",
            withLabel: "In collaboration with",
            partners: [
                "University of Aleppo",
                "University of Damascus",
                "National Union of Syrian Students",
            ],
        },
        launch: {
            eyebrow: "Timeline",
            title: "Launching Soon",
            body:
                "The first public phase of Syrian AI University is currently in preparation. A focused digital experience is coming soon to introduce the initiative and open the door to a broader educational journey in artificial intelligence.",
            highlight: "A new learning journey is about to begin.",
            stages: [
                { k: "01", t: "Foundation", d: "Shaping the identity and educational direction." },
                { k: "02", t: "Soft Launch", d: "Introducing the initiative to the public." },
                { k: "03", t: "Full Experience", d: "Workshops, resources, and community learning." },
            ],
            statuses: { inProgress: "In Progress", upcoming: "Upcoming" },
        },
        cta: {
            eyebrow: "Join the Beginning",
            title: "Be part of the beginning.",
            body:
                "Whether you are a student, a learner, or simply curious about the future of AI, this initiative is being built for you. Follow the launch and discover the beginning of a new educational path.",
            primary: "Join Us",
            secondary: "Discover More",
            notice: "Opens a short form — we'll share launch news with our early community first.",
        },
        contact: {
            eyebrow: "Contact",
            label: "Get in touch",
            email: "info@syrianaiuniversity.com",
        },
        footer: {
            tagline: "A Syrian initiative for practical artificial intelligence education.",
            columns: [
                {
                    title: "Initiative",
                    links: [
                        { label: "About", href: "#about" },
                        { label: "Vision & Mission", href: "#vision" },
                        { label: "Collaboration", href: "#collaboration" },
                    ],
                },
                {
                    title: "Platform",
                    links: [
                        { label: "Offerings", href: "#offerings" },
                        { label: "Audience", href: "#audience" },
                        { label: "Launching Soon", href: "#launch" },
                    ],
                },
            ],
            copyright: "© 2026 Syrian AI University. All rights reserved.",
        },
    },
    ar: {
        meta: {
            brand: "سأو",
            brandFull: "جامعة الذكاء الاصطناعي السورية",
            dir: "rtl",
            lang: "ar",
        },
        nav: {
            links: [
                { id: "about", label: "عن المبادرة" },
                { id: "offerings", label: "ما تقدمه المنصة" },
                { id: "audience", label: "الجمهور المستهدف" },
                { id: "vision", label: "الرؤية" },
                { id: "launch", label: "الإطلاق" },
            ],
            cta: "انضم إلينا",
            home: "الرئيسية",
        },
        hero: {
            badge: "انطلاق قريبًا",
            eyebrow: "مبادرة تعليمية سورية",
            title: "جامعة الذكاء الاصطناعي السورية",
            titleArabic: "Syrian AI University",
            supporting:
                "مبادرة سورية لجعل تعلم الذكاء الاصطناعي عمليًّا ومُتاحًا وجاهزًا للمستقبل.",
            description:
                "جامعة الذكاء الاصطناعي السورية هي مبادرة تعليمية قادمة تهدف إلى مساعدة الطلاب والشباب والمتعلمين من مختلف فئات المجتمع على فهم أدوات الذكاء الاصطناعي واستخدامها بطريقة بسيطة وعملية.",
            primaryCta: "انضم إلينا",
            secondaryCta: "انطلاق قريبًا",
            pillars: ["تعليمية", "عملية", "تطلّعية للمستقبل"],
        },
        about: {
            eyebrow: "عن المبادرة",
            title: "ما هي جامعة الذكاء الاصطناعي السورية؟",
            body:
                "مبادرة سورية شبابية تركّز على نشر الوعي العملي والتعلّم الهادف في الذكاء الاصطناعي، وفتح بوّابة للمتعلمين لفهم الأدوات الحديثة والاستفادة منها في التعليم والإنتاجية والبحث والإبداع.",
            pillars: [
                { label: "عملي", detail: "تعلّم مصمّم للاستخدام الفعلي." },
                { label: "متاح", detail: "نقطة بداية واضحة للفضوليين." },
                { label: "جاهز للمستقبل", detail: "مبادرة محلية برؤية مستقبلية." },
            ],
        },
        whyMatters: {
            eyebrow: "لماذا الآن؟",
            title: "لماذا تهمّ هذه المبادرة؟",
            body:
                "يُشكّل الذكاء الاصطناعي مستقبل التعليم والعمل والبحث والتواصل. وتسعى الجامعة إلى جعل هذه المعرفة أقرب إلى الناس وأسهل في التطبيق.",
            pointers: [
                { k: "١", t: "أقرب من الناس", d: "معرفة مصوّغة حول الاحتياج الحقيقي." },
                { k: "٢", t: "عملية بطبيعتها", d: "من الفضول إلى التطبيق الفعلي." },
                { k: "٣", t: "متجذّرة محليًا", d: "بوّابة سورية نحو عالم الذكاء الاصطناعي." },
            ],
        },
        offerings: {
            eyebrow: "المنصة",
            title: "ماذا ستقدم المنصة؟",
            intro:
                "تجربة تعلّمية عملية تنقل المستخدم من الفضول إلى الفهم والتطبيق.",
            items: [
                { icon: "workshop", title: "ورش عملية", body: "ورش تبسّط الأدوات وتُظهر استخدامها في سياقات فعلية." },
                { icon: "resource", title: "موارد تعليمية", body: "مواد منتقاة لدعم التعلّم الذاتي." },
                { icon: "apply", title: "تطبيقات واقعية", body: "حالات استخدام تدعم الدراسة والعمل والإنتاجية." },
                { icon: "guidance", title: "إرشاد مُيسّر", body: "مسار تعليمي مصمم للمبتدئين والفضوليين." },
            ],
        },
        audience: {
            eyebrow: "لمن هي؟",
            title: "من تستهدف المبادرة؟",
            body: "جامعة الذكاء الاصطناعي السورية مفتوحة لكل مهتم بفهم واستخدام الذكاء الاصطناعي بطريقة حقيقية.",
            items: [
                { title: "طلاب الجامعات", body: "توسيع القدرات الأكاديمية." },
                { title: "الباحثون", body: "دمج الذكاء الاصطناعي في البحث." },
                { title: "المهنيون الشباب", body: "إنجاز أسرع في العمل اليومي." },
                { title: "المبدعون والمبتكرون", body: "بناء وتصميم وسرد." },
                { title: "كل مهتم بالذكاء الاصطناعي", body: "الفضول هو الشرط الوحيد." },
            ],
        },
        vision: { eyebrow: "الرؤية", title: "رؤيتنا", body: "بناء بوّابة تعليمية سورية تجعل تعلم الذكاء الاصطناعي مُتاحًا وعمليًا وملهمًا للأجيال القادمة." },
        mission: { eyebrow: "الرسالة", title: "رسالتنا", body: "تبسيط مفاهيم الذكاء الاصطناعي وبناء بيئة تعليمية تمكّن المتعلمين من الاستفادة من التكنولوجيا بثقة ووضوح." },
        identity: {
            eyebrow: "هدفنا",
            title: "لماذا جامعة الذكاء الاصطناعي السورية؟",
            body: "لأن المستقبل يجب أن يُفهَم لا أن يُخشى، ولأن الوصول إلى المعرفة يجب أن يكون أعدل وأقرب إلى الناس.",
            quote: "المستقبل يُفهَم، ولا يُخشى.",
        },
        collaboration: {
            eyebrow: "تعاون",
            title: "تطوّر بدعم وتعاون مُشترك",
            intro: "تتطوّر هذه المبادرة بالتعاون مع جهات أكاديمية ومجتمعية.",
            leadLabel: "تطوير",
            lead: "الجمعية السورية للذكاء الاصطناعي",
            withLabel: "بالتعاون مع",
            partners: ["جامعة حلب", "جامعة دمشق", "الاتحاد الوطني لطلبة سورية"],
        },
        launch: {
            eyebrow: "الجدول الزمني",
            title: "انطلاق قريبًا",
            body: "تُعّد حاليًا المرحلة الأولى من جامعة الذكاء الاصطناعي السورية، وقريبًا ستُفتح الأبواب للجميع.",
            highlight: "رحلة تعلّمية جديدة على وشك أن تبدأ.",
            stages: [
                { k: "١", t: "التأسيس", d: "صياغة الهوية والاتجاه التعليمي." },
                { k: "٢", t: "إطلاق أولي", d: "تعريف الجمهور بالمبادرة." },
                { k: "٣", t: "التجربة الكاملة", d: "ورش وموارد وتعلّم مجتمعي." },
            ],
            statuses: { inProgress: "قيد التنفيذ", upcoming: "قادم" },
        },
        cta: {
            eyebrow: "انضمّ إلى البداية",
            title: "كن جزءًا من البداية.",
            body: "سواء أكنت طالبًا، أم متعلمًا، أم فضوليًا حول مستقبل الذكاء الاصطناعي، فهذه المبادرة لك.",
            primary: "انضم إلينا",
            secondary: "اكتشف المزيد",
            notice: "يفتح نموذجًا قصيرًا — سنُشارك إعلان الإطلاق أولًا مع مجتمعنا المُبكّر.",
        },
        contact: {
            eyebrow: "تواصل",
            label: "للتواصل معنا",
            email: "info@syrianaiuniversity.com",
        },
        footer: {
            tagline: "مبادرة سورية لتعليم الذكاء الاصطناعي العملي.",
            columns: [
                { title: "المبادرة", links: [ { label: "عن المبادرة", href: "#about" }, { label: "الرؤية والرسالة", href: "#vision" }, { label: "التعاون", href: "#collaboration" } ] },
                { title: "المنصة", links: [ { label: "ما تقدمه", href: "#offerings" }, { label: "الجمهور المستهدف", href: "#audience" }, { label: "انطلاق قريبًا", href: "#launch" } ] },
            ],
            copyright: "© 2026 جامعة الذكاء الاصطناعي السورية. جميع الحقوق محفوظة.",
        },
    },
};

export const ASSETS = {
    logoPrimaryDark: "https://customer-assets.emergentagent.com/job_f806f774-5ff9-4d5b-ae6e-857fc18ef8e6/artifacts/mf0vdows_sau-logo-primary-dark.png.svg",
    logoTransparent: "https://customer-assets.emergentagent.com/job_f806f774-5ff9-4d5b-ae6e-857fc18ef8e6/artifacts/q9an2ujm_sau-logo-transparent.png.png",
};

export const JOIN_URL = "https://forms.gle/mK6CCBQukf4AceJ98";
export const CONTACT_EMAIL = "info@syrianaiuniversity.com";
